import { AboutFreightPricingPortalModal } from "@/components/AboutFreightPricingPortalModal";
import { profileActions } from "@/redux/profile/actions";
import { profileSelectors } from "@/redux/profile/selectors";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { ZIndex } from "@/lib/utils/ZIndexEnum";
import { environment } from "@/config/environment";
import { PublicClientApplication } from "@azure/msal-browser";
import { Column, Indent, Row, useBoolean } from "@stenajs-webui/core";
import {
  ActionMenuItem,
  stenaInfoCircle,
  stenaSignOut,
} from "@stenajs-webui/elements";
import {
  NavBarHeading,
  NavBarUserButton,
  NavBar as StenaNavBar,
} from "@stenajs-webui/panels";
import * as React from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";
import { SidebarMenu } from "../sidebar-menu/SidebarMenu";
import { ApplicationSearch } from "./ApplicationSearch";
import { EnvironmentWarning } from "./EnvironmentWarning";
import { routeFactory } from "@/RouteFactory";

interface Props {
  msalInstance: PublicClientApplication;
}

export const NavBar: React.FC<Props> = ({ msalInstance }) => {
  const dispatch = useDispatch();
  const { fullName, userName, claims } = useSelector(
    profileSelectors.getGlobal
  );
  const [isOpen, open, close] = useBoolean(false);
  const [showAboutModal, setShowAboutModal] = useState(false);

  const handleOnCloseModal = () => {
    setShowAboutModal(false);
  };
  const navigate = useNavigate();

  return (
    <>
      {/**
       * 100vw counts the vertical scrollbar, the page content does not, so on
       * any screen tall enough to scroll this bar ended up a scrollbar wider
       * than everything else and gave the whole app a horizontal scrollbar.
       * The parent column already stretches us to the content width, and
       * sticky left:0 keeps us pinned to the left edge while scrolling.
       */}
      <Column
        left={0}
        position={"sticky"}
        width={"100%"}
        zIndex={ZIndex.medium}
        data-testid={testIdConstants.navBar}
      >
        <SidebarMenu isOpen={isOpen} close={close} claims={claims} />
        <StenaNavBar
          showMenuButton
          onClickMenuButton={open}
          center={
            <ApplicationSearch
              value={null}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderRadius: "var(--swui-max-border-radius)",
                  backgroundColor: "var(--swui-field-bg-enabled)",
                  border: state.isFocused
                    ? "2px solid var(--modern-blue)"
                    : "1px solid var(--swui-select-border-color)",
                }),
              }}
              onChange={(event) => {
                if (event && !Array.isArray(event) && "value" in event) {
                  const customerId = event.value;
                  navigate(
                    routeFactory.productAndPrice.customer.customer({
                      customerId,
                    })
                  );
                }
              }}
            />
          }
          left={
            <Row alignItems={"center"}>
              <Link to={"/"} style={{ textDecoration: "none" }}>
                <NavBarHeading>Freight Pricing Portal</NavBarHeading>
              </Link>
              <Indent />
              {environment.isEnvironmentDev() && <EnvironmentWarning />}
            </Row>
          }
          right={
            userName && (
              <NavBarUserButton
                zIndex={ZIndex.highest}
                data-testid={testIdConstants.userMenuButton}
                username={fullName}
                renderItems={() => (
                  <>
                    <ActionMenuItem
                      leftIcon={stenaInfoCircle}
                      label="About Freight Pricing Portal"
                      onClick={() => setShowAboutModal(true)}
                    />
                    <ActionMenuItem
                      leftIcon={stenaSignOut}
                      label={"Logout"}
                      data-testid={testIdConstants.logOutButton}
                      onClick={async () => {
                        localStorage.removeItem("AgreementTabSelected");
                        dispatch(profileActions.resetGlobalState());
                        await msalInstance.initialize();
                        await msalInstance.logoutRedirect();
                      }}
                    />
                  </>
                )}
              />
            )
          }
        />
      </Column>
      <AboutFreightPricingPortalModal
        user={userName}
        visible={showAboutModal}
        onClose={handleOnCloseModal}
      />
    </>
  );
};
